import { useEffect, useMemo, useState } from "react";

import { fetchLast12HoursCases } from "../../store/allocation/readyCasesSlice";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { isEmployeeInvolvedInCase } from "../../utils/employeeNames";
import MyCasesToggle, {
  useMyCasesToggle,
} from "../../components/allocation-dashboard/MyCasesToggle";

import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from "react-icons/fa";

import UploadStatusBadge from "../../components/allocation-dashboard/UploadStatusBadge";
import UploadStatusLegend from "../../components/allocation-dashboard/UploadStatusLegend";
import { formatDateTimeIST } from "../../utils/dateTime";
import { getUploadStatusInfo } from "../../utils/uploadStatus";
import { RedesignScopeTag } from "../../components/admin/unitsTableShared";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminSelect,
  AdminMetaRow,
  AdminClearFilters,
  AdminTableCard,
  AdminTableState,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
} from "@tanstack/react-table";

const columnFilterInputClass =
  "rounded-lg border border-[#DDDDD8] bg-white px-1.5 py-1 text-[11px] text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 w-full";

const AllocationUpdates = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { loading, error, last12HoursCases } = useAppSelector(
    (state) => state.readycases,
  );
  const { showToggle, myCasesOnly, enabled: myCasesEnabled, setEnabled: setMyCasesEnabled } =
    useMyCasesToggle(employee);

  const visibleCases = useMemo(
    () =>
      (last12HoursCases || []).filter((item) =>
        myCasesOnly
          ? isEmployeeInvolvedInCase(item, employee?.emp_name)
          : true,
      ),
    [last12HoursCases, employee?.emp_name, myCasesOnly],
  );

  const [globalFilter, setGlobalFilter] = useState("");
  const [uploadFilter, setUploadFilter] = useState<
    "ALL" | "PENDING" | "UPLOADED"
  >("ALL");

  useEffect(() => {
    dispatch(fetchLast12HoursCases());
  }, [dispatch]);

  const data = useMemo(() => {
    let cases = visibleCases;

    if (uploadFilter === "PENDING") {
      cases = cases.filter((c) => c.status === "Ready to Upload");
    } else if (uploadFilter === "UPLOADED") {
      cases = cases.filter((c) => c.status?.startsWith("Uploaded"));
    }

    return cases;
  }, [visibleCases, uploadFilter]);

  const uploadCounts = useMemo(() => {
    const pending = visibleCases.filter((c) => c.status === "Ready to Upload").length;
    const uploaded = visibleCases.filter((c) =>
      c.status?.startsWith("Uploaded"),
    ).length;
    return { pending, uploaded };
  }, [visibleCases]);

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "case_id",
        header: "Case ID",
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-1">
            <span>{row.original.case_id}</span>
            <RedesignScopeTag labels={row.original.redesign_category} />
          </div>
        ),
      },
      {
        accessorKey: "allocated_Designer",
        header: "Designer",
      },
      {
        accessorKey: "allocated_Captain",
        header: "Captain",
      },
      {
        accessorKey: "allocated_by",
        header: "Allocated By",
      },
      {
        accessorKey: "type",
        header: "Type",
      },
      {
        accessorKey: "status",
        header: "Status",
      },
      {
        id: "upload_status",
        header: "Upload Status",
        accessorFn: (row) => row.status,
        cell: ({ getValue }) => (
          <UploadStatusBadge status={getValue() as string} />
        ),
      },
      {
        accessorKey: "QC1",
        header: "QC1",
      },
      {
        accessorKey: "QC2",
        header: "QC2",
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        cell: ({ getValue }) => formatDateTimeIST(getValue() as string),
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const hasActiveFilters = Boolean(globalFilter.trim()) || uploadFilter !== "ALL";
  const clearFilters = () => {
    setGlobalFilter("");
    setUploadFilter("ALL");
  };

  return (
    <>
      <Helmet>
        <title>12 Hour Cases</title>
      </Helmet>

      <div className="p-4">
        <AdminPageShell>
          <AdminPageHeader
            title="Cases Allocated in Last 12 Hours"
            subtitle="Track allocation and upload progress for recent cases."
          >
            <AdminSearchInput
              value={globalFilter ?? ""}
              onChange={setGlobalFilter}
              placeholder="Search…"
              className="w-full sm:w-[220px]"
            />
            <AdminSelect
              value={uploadFilter}
              onChange={(value) =>
                setUploadFilter(value as "ALL" | "PENDING" | "UPLOADED")
              }
            >
              <option value="ALL">All Upload Status</option>
              <option value="PENDING">
                Pending Upload ({uploadCounts.pending})
              </option>
              <option value="UPLOADED">
                Uploaded ({uploadCounts.uploaded})
              </option>
            </AdminSelect>
            <MyCasesToggle
              show={showToggle}
              enabled={myCasesEnabled}
              empName={employee?.emp_name}
              onChange={setMyCasesEnabled}
            />
            <button
              type="button"
              onClick={() => navigate("/dashboard/allocation/unallocated")}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-white/15 border border-white/25 text-white px-4 py-2.5 text-xs font-medium hover:bg-white/20 transition"
            >
              <FaArrowLeft className="text-[10px]" />
              Back to allocation
            </button>
          </AdminPageHeader>

          <AdminMetaRow
            left={
              <>
                {loading ? (
                  <span>Loading cases…</span>
                ) : (
                  <span>
                    {table.getFilteredRowModel().rows.length} case
                    {table.getFilteredRowModel().rows.length === 1 ? "" : "s"}
                    {hasActiveFilters ? " matched" : " in the last 12 hours"}
                  </span>
                )}
                {hasActiveFilters ? (
                  <AdminClearFilters onClick={clearFilters} />
                ) : null}
              </>
            }
          />

          <UploadStatusLegend />

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Error: {error}
            </div>
          )}

          {!loading && !error && (
            <AdminTableCard>
              <table className="w-full table-fixed border-collapse text-sm">
                <thead className="bg-[#FDFBF2]">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className="border-b border-[#E8E8E4]"
                    >
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`${adminTableHead} align-top w-[140px]`}
                        >
                          <div className="flex flex-col gap-1.5">
                            <div
                              className="flex items-center justify-between cursor-pointer select-none normal-case tracking-normal font-semibold text-[#4A4A48]"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <span className="truncate text-[11px] uppercase tracking-wider text-[#8A8A85]">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </span>

                              <span className="ml-1 text-[#2fa9bf]">
                                {{
                                  asc: "▲",
                                  desc: "▼",
                                }[header.column.getIsSorted() as string] ?? ""}
                              </span>
                            </div>

                            {header.column.getCanFilter() && (
                              <input
                                type="text"
                                value={
                                  (header.column.getFilterValue() as string) ??
                                  ""
                                }
                                onChange={(e) =>
                                  header.column.setFilterValue(e.target.value)
                                }
                                placeholder="Filter"
                                className={columnFilterInputClass}
                              />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <AdminTableState
                      colSpan={columns.length}
                      variant="empty"
                      message="No data found"
                    />
                  ) : (
                    table.getRowModel().rows.map((row) => {
                      const uploadInfo = getUploadStatusInfo(
                        row.original.status,
                      );
                      return (
                        <tr
                          key={row.id}
                          className={`border-b border-[#F0F0EC] last:border-0 transition-colors ${
                            uploadInfo.rowClass || "hover:bg-[#FAFAF8]"
                          }`}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className={`${adminTableCell} break-words align-top`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              ) || cell.getValue()?.toString()}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              <div className="flex flex-col md:flex-row items-center justify-between gap-3 border-t border-[#E8E8E4] bg-[#FDFBF2] p-3 text-xs text-[#4A4A48]">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] disabled:opacity-40 disabled:cursor-not-allowed transition"
                    aria-label="Previous page"
                  >
                    <FaChevronLeft className="text-xs" />
                  </button>

                  <button
                    type="button"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] disabled:opacity-40 disabled:cursor-not-allowed transition"
                    aria-label="Next page"
                  >
                    <FaChevronRight className="text-xs" />
                  </button>
                </div>

                <span className="tabular-nums">
                  Page{" "}
                  <strong>
                    {table.getState().pagination.pageIndex + 1} of{" "}
                    {table.getPageCount() || 1}
                  </strong>
                </span>

                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                  className="rounded-lg border border-[#DDDDD8] bg-white px-2 py-1.5 text-xs text-[#4A4A48] outline-none focus:border-[#2fa9bf] cursor-pointer"
                >
                  {[10, 20, 50, 100].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </AdminTableCard>
          )}
        </AdminPageShell>
      </div>
    </>
  );
};

export default AllocationUpdates;
